import React, { useState, useEffect, useRef } from "react";
import {
  ListChecks, FileBarChart2, CalendarClock, Sparkles, Download, Check, Zap,
  LayoutGrid, CalendarDays, ClipboardList, Users, Car, FileText, TrendingUp, ArrowRight,
} from "lucide-react";

const ROTATE_MS = 7500;
const TICK_MS = 50;

const CAPABILITIES = [
  {
    id: "pilot",
    icon: LayoutGrid,
    title: "Poste de pilotage IA",
    desc: "Suggestions, raccourcis, analyses chiffrées et actions préparées : Copilot connaît votre flotte, vos réservations et vos revenus — et répond en français, en quelques secondes.",
  },
  {
    id: "tasks",
    icon: ListChecks,
    title: "Attribution automatique des tâches",
    desc: "L'IA assigne les préparations, livraisons et états des lieux à votre équipe. Toute seule, sans que vous demandiez.",
  },
  {
    id: "report",
    icon: FileBarChart2,
    title: "Rapports financiers, sans demander",
    desc: "Chaque lundi, votre rapport est prêt. Ou demandez-le à l'IA quand vous voulez, en une phrase.",
  },
  {
    id: "planning",
    icon: CalendarClock,
    title: "Le planning du jour, prêt à 7h",
    desc: "L'IA organise la journée avant même votre café : départs, retours, préparations à venir.",
  },
];

const TICKER = [
  "07:00 — Planning de la journée généré",
  "07:04 — 3 tâches attribuées à Karim",
  "08:00 — Rapport financier envoyé",
  "09:12 — Relance caution M. Petit envoyée",
  "10:30 — État des lieux assigné à Awa",
  "11:45 — Alerte entretien Clio V programmée",
];

function useTypewriter(text, active, speed = 42) {
  const [count, setCount] = useState(0);
  useEffect(() => { setCount(0); }, [text]);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => {
      setCount((c) => (c >= text.length ? c : c + 1));
    }, speed);
    return () => clearInterval(id);
  }, [text, active, speed]);
  return { typed: text.slice(0, count), done: count >= text.length };
}

/* ─── Rangées & briques partagées ─── */
function MiniRow({ left, title, sub, right, delay = 0 }) {
  return (
    <div
      className="flex items-center gap-3 rounded-xl bg-white/[0.06] border border-white/10 px-3.5 py-2.5"
      style={{ animation: "jnMaterialize 0.55s cubic-bezier(0.22,1,0.36,1) both", animationDelay: `${delay}ms` }}
    >
      {left}
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold text-white truncate">{title}</div>
        <div className="text-[11px] text-slate-400">{sub}</div>
      </div>
      {right}
    </div>
  );
}

/* ─── Scène 1 : Poste de pilotage IA (interface Copilot interactive) ─── */
const PILOT_SHORTCUTS = [
  { id: "calendrier", label: "Calendrier", icon: CalendarDays, q: "Montre-moi mon planning du jour" },
  { id: "reservations", label: "Réservations", icon: ClipboardList, q: "Quelle est ma prochaine réservation ?" },
  { id: "clients", label: "Clients", icon: Users, q: "Qui est mon meilleur client ?" },
  { id: "vehicules", label: "Véhicules", icon: Car, q: "Quel véhicule est le plus demandé ?" },
  { id: "devis", label: "Devis", icon: FileText, q: "Où en est le devis de Mme Rousseau ?" },
  { id: "revenus", label: "Revenus", icon: TrendingUp, q: "Combien ai-je encaissé cette semaine ?" },
];

function PilotAnswer({ tab }) {
  const card = "rounded-xl bg-gradient-to-br from-violet-600/20 to-fuchsia-600/10 border border-violet-400/25 p-3.5";
  const anim = { animation: "jnMaterialize 0.55s cubic-bezier(0.22,1,0.36,1) both", animationDelay: "180ms" };
  if (tab === "calendrier") {
    return (
      <div className="space-y-2" style={anim}>
        <MiniRow left={<span className="text-[11px] font-bold text-violet-300 w-10 shrink-0">08:30</span>} title="Départ Clio V — M. Petit" sub="Contrat signé" right={<Check className="h-3.5 w-3.5 text-emerald-300 shrink-0" strokeWidth={3} />} />
        <MiniRow left={<span className="text-[11px] font-bold text-violet-300 w-10 shrink-0">14:30</span>} title="Retour 208 — Mme Diallo" sub="État des lieux à prévoir" right={<span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 text-[10px] font-bold shrink-0">À faire</span>} delay={130} />
      </div>
    );
  }
  if (tab === "reservations") {
    return (
      <div className={card} style={anim}>
        <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-violet-300 mb-1.5">Prochaine réservation</div>
        <div className="text-[13px] font-bold text-white">BMW X1 — M. Martin</div>
        <div className="text-[11px] text-slate-400 mt-0.5">Demain 10:00 · 4 jours · Agence centrale</div>
        <span className="mt-2 inline-flex px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[10px] font-bold">Confirmée · acompte payé</span>
      </div>
    );
  }
  if (tab === "clients") {
    return (
      <div className={card} style={anim}>
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 rounded-full bg-violet-600 flex items-center justify-center text-[11px] font-bold text-white shrink-0">SL</span>
          <div>
            <div className="text-[13px] font-bold text-white">Sophie Laurent</div>
            <div className="text-[11px] text-slate-400">8 locations · fidèle depuis 2025</div>
          </div>
        </div>
        <div className="mt-2 text-[11px] text-slate-300">Copilot suggère : proposer un tarif fidélité sur sa prochaine réservation.</div>
      </div>
    );
  }
  if (tab === "vehicules") {
    return (
      <div className={card} style={anim}>
        <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-violet-300 mb-1.5">Véhicule le plus demandé</div>
        <div className="text-[13px] font-bold text-white">Renault Clio V</div>
        <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full w-[82%] rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-400" />
        </div>
        <div className="mt-1.5 text-[11px] text-slate-400">Très forte occupation ce mois-ci · entretien à jour</div>
      </div>
    );
  }
  if (tab === "devis") {
    return (
      <div className={card} style={anim}>
        <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-violet-300 mb-1.5">Devis — Mme Rousseau</div>
        <div className="text-[13px] font-bold text-white">Peugeot 3008 · 5 jours · 425 €</div>
        <div className="text-[11px] text-slate-400 mt-0.5">Envoyé il y a 3 jours · pas encore accepté</div>
        <button className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-violet-200 hover:text-white transition-colors">
          Relancer par email <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    );
  }
  return (
    <div className={card} style={anim}>
      <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-violet-300 mb-2">Encaissements — cette semaine</div>
      <div className="grid grid-cols-3 gap-2">
        {[["Encaissé", "3 240 €"], ["En attente", "680 €"], ["Cautions", "2 000 €"]].map(([l, v]) => (
          <div key={l} className="rounded-lg bg-white/[0.06] px-2 py-1.5">
            <div className="text-[9px] text-slate-400">{l}</div>
            <div className="text-[12px] font-bold text-white">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ScenePilot() {
  const [tab, setTab] = useState("calendrier");
  const current = PILOT_SHORTCUTS.find((s) => s.id === tab);
  return (
    <div data-testid="ai-scene-pilot">
      {/* Raccourcis façon Copilot réel */}
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="ai-pilot-shortcuts">
        {PILOT_SHORTCUTS.map((s) => (
          <button
            key={s.id}
            onClick={() => setTab(s.id)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-semibold transition-colors duration-200 ${
              tab === s.id
                ? "bg-violet-600 border-violet-500 text-white"
                : "bg-white/[0.05] border-white/10 text-slate-300 hover:bg-white/[0.1] hover:text-white"
            }`}
            data-testid={`ai-pilot-shortcut-${s.id}`}
          >
            <s.icon className="h-3 w-3" /> {s.label}
          </button>
        ))}
      </div>
      <div key={tab}>
        {/* Question utilisateur */}
        <div className="rounded-xl bg-white/[0.06] border border-white/10 px-3.5 py-2.5 flex items-center gap-3" style={{ animation: "jnMaterialize 0.4s cubic-bezier(0.22,1,0.36,1) both" }}>
          <span className="h-6 w-6 rounded-full bg-slate-700 flex items-center justify-center text-[9px] font-bold text-white shrink-0">JD</span>
          <span className="text-[12px] text-slate-200 font-medium">{current.q}</span>
        </div>
        {/* Réponse Copilot */}
        <div className="mt-2.5 flex items-start gap-2.5">
          <span className="h-6 w-6 rounded-lg bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center shrink-0 mt-1">
            <Sparkles className="h-3 w-3 text-white" />
          </span>
          <div className="flex-1 min-w-0">
            <PilotAnswer tab={tab} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Scène 2 : tâches auto ─── */
function SceneTasks() {
  return (
    <div className="space-y-2.5" data-testid="ai-scene-tasks">
      <div className="flex items-center gap-2 text-[11px] font-bold tracking-[0.14em] uppercase text-violet-300 mb-3">
        <Zap className="h-3.5 w-3.5" /> Mode autonome — aucune action requise
      </div>
      <MiniRow left={<span className="h-8 w-8 rounded-full bg-violet-600 flex items-center justify-center text-[11px] font-bold text-white shrink-0">KB</span>} title="Préparation Clio V — départ 14h" sub="Karim · aujourd'hui" right={<span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[10px] font-bold shrink-0">Assignée</span>} delay={150} />
      <MiniRow left={<span className="h-8 w-8 rounded-full bg-fuchsia-600 flex items-center justify-center text-[11px] font-bold text-white shrink-0">AD</span>} title="Livraison aéroport — BMW X1" sub="Awa · 10:00" right={<span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[10px] font-bold shrink-0">Assignée</span>} delay={400} />
      <MiniRow left={<span className="h-8 w-8 rounded-full bg-indigo-500 flex items-center justify-center text-[11px] font-bold text-white shrink-0">YM</span>} title="État des lieux retour — 208" sub="Yohan · 16:30" right={<span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[10px] font-bold shrink-0">Assignée</span>} delay={650} />
    </div>
  );
}

/* ─── Scène 3 : rapport ─── */
function SceneReport({ active }) {
  const { typed, done } = useTypewriter("Génère le rapport financier de mars", active);
  return (
    <div data-testid="ai-scene-report">
      <div className="rounded-xl bg-white/[0.06] border border-white/10 px-4 py-3 flex items-center gap-3">
        <span className="h-7 w-7 rounded-full bg-slate-700 flex items-center justify-center text-[10px] font-bold text-white shrink-0">JD</span>
        <span className="text-[13px] text-slate-200 font-medium">
          {typed}
          <span className="inline-block w-[2px] h-[14px] bg-violet-400 ml-0.5 align-middle" style={{ animation: "jnCaret 0.9s step-end infinite" }} />
        </span>
      </div>
      {done && (
        <div
          className="mt-3 rounded-xl bg-gradient-to-br from-violet-600/20 to-fuchsia-600/10 border border-violet-400/25 p-4"
          style={{ animation: "jnMaterialize 0.55s cubic-bezier(0.22,1,0.36,1) both", animationDelay: "250ms" }}
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-3.5 w-3.5 text-violet-300" />
            <span className="text-[12px] font-bold text-white">Rapport financier — Mars</span>
          </div>
          <div className="grid grid-cols-3 gap-2.5">
            {[
              ["CA total", "24 380 €", "+12 %"],
              ["Marge nette", "8 940 €", "+9 %"],
              ["Top véhicule", "Clio V", "2 310 €"],
            ].map(([l, v, s]) => (
              <div key={l} className="rounded-lg bg-white/[0.06] px-2.5 py-2">
                <div className="text-[10px] text-slate-400">{l}</div>
                <div className="text-[13px] font-bold text-white">{v}</div>
                <div className="text-[10px] text-emerald-300 font-semibold">{s}</div>
              </div>
            ))}
          </div>
          <button className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-violet-200 hover:text-white transition-colors">
            <Download className="h-3 w-3" /> Télécharger le PDF
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Scène 4 : planning ─── */
function ScenePlanning() {
  return (
    <div className="space-y-2.5" data-testid="ai-scene-planning">
      <div className="flex items-center gap-2 text-[11px] font-bold tracking-[0.14em] uppercase text-violet-300 mb-3">
        <CalendarClock className="h-3.5 w-3.5" /> Planning du mercredi 12 juin — généré à 06:58
      </div>
      <MiniRow left={<span className="text-[12px] font-bold text-violet-300 w-11 shrink-0">08:30</span>} title="Départ Clio V — M. Petit" sub="Contrat signé · caution OK" right={<Check className="h-3.5 w-3.5 text-emerald-300 shrink-0" strokeWidth={3} />} delay={150} />
      <MiniRow left={<span className="text-[12px] font-bold text-violet-300 w-11 shrink-0">10:00</span>} title="Livraison aéroport — BMW X1" sub="Assignée à Awa" right={<Check className="h-3.5 w-3.5 text-emerald-300 shrink-0" strokeWidth={3} />} delay={400} />
      <MiniRow left={<span className="text-[12px] font-bold text-violet-300 w-11 shrink-0">14:30</span>} title="Retour + état des lieux — 208" sub="Assigné à Yohan" right={<Check className="h-3.5 w-3.5 text-emerald-300 shrink-0" strokeWidth={3} />} delay={650} />
    </div>
  );
}

export default function AICommandCenter() {
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.25 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  // Ticker de progression — pausable, reprend exactement où il s'était arrêté
  useEffect(() => {
    if (!inView || paused) return;
    const id = setInterval(() => {
      setProgress((p) => Math.min(p + TICK_MS / ROTATE_MS, 1));
    }, TICK_MS);
    return () => clearInterval(id);
  }, [inView, paused]);

  useEffect(() => {
    if (progress >= 1) {
      setActive((a) => (a + 1) % CAPABILITIES.length);
      setProgress(0);
    }
  }, [progress]);

  const selectScenario = (i) => {
    setActive(i);
    setProgress(0);
  };

  return (
    <div
      ref={ref}
      className="relative rounded-3xl overflow-hidden bg-[#0B0B16] border border-white/10"
      data-testid="ai-command-center"
    >
      {/* Halos violets + grain */}
      <div aria-hidden className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-violet-600/25 blur-[110px]" />
      <div aria-hidden className="absolute -bottom-40 right-0 h-96 w-96 rounded-full bg-fuchsia-600/15 blur-[120px]" />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.25] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.45'/%3E%3C/svg%3E\")",
        }}
      />

      <div
        className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 p-8 md:p-12 lg:p-16"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
        data-testid="ai-interactive-zone"
      >
        {/* ── Gauche : titre + capacités auto-rotatives ── */}
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/15 border border-violet-400/25 text-violet-200 text-[11px] font-bold tracking-[0.15em] uppercase">
            <Sparkles className="h-3 w-3" /> Junago Copilot
          </div>
          <h3 className="mt-6 font-heading text-3xl md:text-4xl lg:text-[2.75rem] font-medium tracking-tight leading-[1.1] text-white">
            Une IA qui travaille <span className="text-violet-400">pour vous.</span>
            <br />
            Même quand vous ne demandez rien.
          </h3>
          <p className="mt-4 text-[13px] text-slate-400 leading-relaxed">
            Junago pilote. Junago Copilot assiste. Basculez de l&apos;un à l&apos;autre à tout moment, d&apos;un simple clic.
          </p>
          <div className="mt-10 space-y-1">
            {CAPABILITIES.map((cap, i) => {
              const Icon = cap.icon;
              const isActive = i === active;
              return (
                <button
                  key={cap.id}
                  onClick={() => selectScenario(i)}
                  className={`relative w-full text-left rounded-2xl px-5 py-4 transition-colors duration-300 ${
                    isActive ? "bg-white/[0.07]" : "hover:bg-white/[0.04]"
                  }`}
                  data-testid={`ai-cap-${cap.id}`}
                >
                  <div className="flex items-start gap-3.5">
                    <span
                      className={`mt-0.5 h-8 w-8 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-300 ${
                        isActive ? "bg-violet-600 text-white" : "bg-white/[0.07] text-slate-400"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <div>
                      <div className={`text-[15px] font-semibold transition-colors duration-300 ${isActive ? "text-white" : "text-slate-400"}`}>
                        {cap.title}
                      </div>
                      <div
                        className={`grid transition-[grid-template-rows,opacity] duration-500 ease-out ${
                          isActive ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                        }`}
                      >
                        <div className="overflow-hidden">
                          <p className="pt-1.5 text-[13px] text-slate-400 leading-relaxed">{cap.desc}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Barre de progression — pilotée par l'état (pausable) */}
                  <div className="mt-3 ml-[46px] h-[2px] rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-400 rounded-full"
                      style={{ width: isActive ? `${progress * 100}%` : "0%" }}
                      data-testid={isActive ? "ai-progress-active" : undefined}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Droite : console IA vivante ── */}
        <div className="flex flex-col min-w-0">
          <div className="flex-1 rounded-2xl bg-black/40 border border-white/10 backdrop-blur-sm overflow-hidden flex flex-col" data-testid="ai-console">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
              <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
              <span className="ml-3 text-[11px] font-semibold text-slate-300">Junago Copilot</span>
              <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] font-bold text-emerald-300">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> En ligne
              </span>
            </div>
            <div className="flex-1 p-4 md:p-5 min-h-[290px]">
              <div key={active}>
                {active === 0 && <ScenePilot />}
                {active === 1 && <SceneTasks />}
                {active === 2 && <SceneReport active={inView && !paused} />}
                {active === 3 && <ScenePlanning />}
              </div>
            </div>
            {/* Ticker mode autonome */}
            <div className="border-t border-white/10 bg-white/[0.03] py-2.5 overflow-hidden" data-testid="ai-autonomous-ticker">
              <div className="flex items-center gap-2 px-4 pb-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-violet-400 animate-pulse" />
                <span className="text-[9px] font-bold tracking-[0.16em] uppercase text-violet-300">Mode autonome — en direct</span>
              </div>
              <div className="relative whitespace-nowrap">
                <div className="inline-flex gap-8" style={{ animation: "jnTicker 28s linear infinite", animationPlayState: paused ? "paused" : "running" }}>
                  {[...TICKER, ...TICKER].map((t, i) => (
                    <span key={i} className="text-[11px] text-slate-400 inline-flex items-center gap-2">
                      <Check className="h-3 w-3 text-emerald-400" strokeWidth={3} /> {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
